import { conics, intersect, lines } from '../geometry';
import { angle, clamp, EPSILON, type Vec2, vec } from '../math';

import { type Curve } from './evaluated';

/**
 * Parametrisation of curves, and intersection between any two of them.
 *
 * This module is what makes locus points work — the mechanism ADR-0003 uses to keep
 * "on line", "on circle" and "on segment" **exact** rather than solver-approximate. A
 * locus point stores a parameter, not a coordinate; its position is always
 * `pointAt(host, t)`, so it cannot drift off its host no matter how the host moves.
 *
 * Parameter conventions, chosen so that dragging feels natural for each shape:
 *
 * | Curve   | `t` means                  | Range      |
 * | ------- | -------------------------- | ---------- |
 * | Line    | signed distance from origin | `(-∞, ∞)` |
 * | Ray     | distance from origin        | `[0, ∞)`  |
 * | Segment | fraction of the length      | `[0, 1]`   |
 * | Circle  | fraction of a full turn     | `[0, 1)`   |
 * | Arc     | fraction of the sweep       | `[0, 1]`   |
 * | Ellipse | fraction of eccentric angle | `[0, 1)`   |
 *
 * Closed curves use `[0, 1)` rather than raw radians so that a locus point on a circle
 * behaves like one on a segment from the UI's point of view.
 */

const TAU = Math.PI * 2;

/** The point on `curve` at parameter `t`. */
export function pointAt(curve: Curve, t: number): Vec2 {
  switch (curve.kind) {
    case 'line':
      return lines.pointOnLine(curve.line, t);
    case 'ray':
      return lines.pointOnRay(curve.ray, Math.max(0, t));
    case 'segment':
      return lines.pointOnSegment(curve.segment, clamp(t, 0, 1));
    case 'circle':
      return conics.pointOnCircle(curve.circle, t * TAU);
    case 'arc':
      return conics.pointOnArc(curve.arc, clamp(t, 0, 1));
    case 'ellipse':
      return conics.pointOnEllipse(curve.ellipse, t * TAU);
  }
}

/**
 * The parameter of the point on `curve` closest to `p`.
 *
 * This is the other half of a locus point: dragging projects the pointer onto the host
 * and stores the resulting parameter. Because the projection is exact, the point lands
 * exactly on the curve rather than near it.
 */
export function parameterAt(curve: Curve, p: Vec2): number {
  switch (curve.kind) {
    case 'line':
      return lines.parameterOnLine(curve.line, p);

    case 'ray':
      return Math.max(0, vec.dot(vec.subtract(p, curve.ray.origin), curve.ray.direction));

    case 'segment':
      return clamp(lines.parameterOnSegment(curve.segment, p), 0, 1);

    case 'circle': {
      const theta = conics.angleOnCircle(curve.circle, p);
      return angle.normalize(theta) / TAU;
    }

    case 'arc': {
      const sweep = conics.arcSweep(curve.arc);
      if (sweep <= EPSILON) return 0;
      const theta = vec.angle(vec.subtract(p, curve.arc.center));
      const offset = curve.arc.counterclockwise
        ? angle.normalize(curve.arc.startAngle - theta)
        : angle.normalize(theta - curve.arc.startAngle);
      // Beyond the far end, the shorter way back is to the start; clamping the raw
      // offset would otherwise pin every outside angle to the end cap.
      if (offset > sweep) {
        const past = offset - sweep;
        const beforeStart = TAU - offset;
        return past <= beforeStart ? 1 : 0;
      }
      return offset / sweep;
    }

    case 'ellipse': {
      const closest = conics.closestPointOnEllipse(curve.ellipse, p);
      const unit = conics.toUnitCircleSpace(curve.ellipse, closest);
      return angle.normalize(Math.atan2(unit.y, unit.x)) / TAU;
    }
  }
}

/** The point on `curve` closest to `p`. */
export function closestPoint(curve: Curve, p: Vec2): Vec2 {
  switch (curve.kind) {
    case 'line':
      return lines.closestPointOnLine(curve.line, p);
    case 'ray':
      return lines.closestPointOnRay(curve.ray, p);
    case 'segment':
      return lines.closestPointOnSegment(curve.segment, p);
    case 'circle':
      return conics.closestPointOnCircle(curve.circle, p);
    case 'arc':
      return conics.closestPointOnArc(curve.arc, p);
    case 'ellipse':
      return conics.closestPointOnEllipse(curve.ellipse, p);
  }
}

export function distanceTo(curve: Curve, p: Vec2): number {
  return vec.distance(p, closestPoint(curve, p));
}

/**
 * Intersections between any two curves.
 *
 * Dispatches over the 6x6 matrix of curve kinds onto the routines in
 * `domain/geometry/intersect`, which document their own ordering. The order of the
 * result is therefore stable, which is what lets an `Intersection` source store "the
 * second one" as an index and have it mean the same thing on every reload.
 *
 * Ray cases are handled by intersecting the containing line and then filtering to the
 * forward half — the alternative is another dozen bespoke routines for no gain.
 */
export function intersectCurves(a: Curve, b: Curve): readonly Vec2[] {
  const direct = intersectDirect(a, b);
  if (direct !== null) return direct;

  const swapped = intersectDirect(b, a);
  if (swapped !== null) return swapped;

  // Unreachable for the current curve set; kept as an explicit empty result rather
  // than a throw so a future curve kind degrades to "no intersection" instead of
  // crashing the whole evaluation sweep.
  return [];
}

function intersectDirect(a: Curve, b: Curve): readonly Vec2[] | null {
  switch (a.kind) {
    case 'line':
      switch (b.kind) {
        case 'line': {
          const hit = intersect.lineLine(a.line, b.line);
          return hit === null ? [] : [hit];
        }
        case 'ray': {
          const hit = intersect.lineRay(a.line, b.ray);
          return hit === null ? [] : [hit];
        }
        case 'segment': {
          const hit = intersect.lineSegment(a.line, b.segment);
          return hit === null ? [] : [hit];
        }
        case 'circle':
          return intersect.lineCircle(a.line, b.circle);
        case 'arc':
          return intersect.lineArc(a.line, b.arc);
        case 'ellipse':
          return intersect.lineEllipse(a.line, b.ellipse);
      }
      break;

    case 'ray':
      switch (b.kind) {
        case 'ray': {
          const hit = intersect.rayRay(a.ray, b.ray);
          return hit === null ? [] : [hit];
        }
        case 'segment': {
          const hit = intersect.raySegment(a.ray, b.segment);
          return hit === null ? [] : [hit];
        }
        case 'circle':
          return intersect.rayCircle(a.ray, b.circle);
        case 'arc':
          return forwardOfRay(a.ray, intersect.lineArc(rayLine(a.ray), b.arc));
        case 'ellipse':
          return forwardOfRay(a.ray, intersect.lineEllipse(rayLine(a.ray), b.ellipse));
        case 'line':
          return null; // handled by the swapped pass
      }
      break;

    case 'segment':
      switch (b.kind) {
        case 'segment': {
          const hit = intersect.segmentSegment(a.segment, b.segment);
          return hit === null ? [] : [hit];
        }
        case 'circle':
          return intersect.segmentCircle(a.segment, b.circle);
        case 'arc':
          return intersect.segmentArc(a.segment, b.arc);
        case 'ellipse':
          return intersect.segmentEllipse(a.segment, b.ellipse);
        case 'line':
        case 'ray':
          return null;
      }
      break;

    case 'circle':
      switch (b.kind) {
        case 'circle':
          return intersect.circleCircle(a.circle, b.circle);
        case 'arc':
          return intersect.circleArc(a.circle, b.arc);
        case 'ellipse':
          return intersect.circleEllipse(a.circle, b.ellipse);
        case 'line':
        case 'ray':
        case 'segment':
          return null;
      }
      break;

    case 'arc':
      switch (b.kind) {
        case 'arc':
          return intersect.arcArc(a.arc, b.arc);
        case 'ellipse':
          return intersect
            .circleEllipse(conics.arcToCircle(a.arc), b.ellipse)
            .filter((p) => conics.isAngleOnArc(a.arc, vec.angle(vec.subtract(p, a.arc.center))));
        case 'line':
        case 'ray':
        case 'segment':
        case 'circle':
          return null;
      }
      break;

    case 'ellipse':
      switch (b.kind) {
        case 'ellipse':
          return intersect.ellipseEllipse(a.ellipse, b.ellipse);
        case 'line':
        case 'ray':
        case 'segment':
        case 'circle':
        case 'arc':
          return null;
      }
      break;
  }
  return null;
}

function rayLine(ray: { origin: Vec2; direction: Vec2 }) {
  return { origin: ray.origin, direction: ray.direction };
}

function forwardOfRay(
  ray: { origin: Vec2; direction: Vec2 },
  points: readonly Vec2[]
): readonly Vec2[] {
  return points.filter((p) => vec.dot(vec.subtract(p, ray.origin), ray.direction) >= -EPSILON);
}
